// Экран «Отчёты» — четырнадцатый экран нового фронта (п.20, 2026-09-07).
// Базовые планы (базлайны): зафиксировать текущий расчёт, сравнить с любым из них
// и увидеть отклонения по работам, выгрузить план в Excel/CSV.
import { useCallback, useEffect, useState } from 'react';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { MspExport } from '../MspExport';

type Baseline = Data<'/api/projects/{pid}/baselines', 'get'>[number];
type Deviations = Data<'/api/projects/{pid}/report/deviations', 'get'>;
/** строка отклонений: ключи описаны свободной формой, приводим к нужным полям */
type DevRow = {
  korpus?: string; rabota?: string; etazhej?: number;
  pozzhe?: number; ranshe?: number; maks_sdvig_dnej?: number | null; min_sdvig_dnej?: number | null;
};

export function Rep({ ctx }: { ctx: ShellCtx }) {
  const [list, setList] = useState<Baseline[]>([]);
  const [cur, setCur] = useState<string>('');
  const [dev, setDev] = useState<Deviations | null>(null);
  const [name, setName] = useState('');
  const can = ctx.canPlan;
  const fail = (m: string) => ctx.onError?.(m);

  const loadList = useCallback(async () => {
    const r = await call<Baseline[]>('get', p('/api/projects/{pid}/baselines', { pid: ctx.pid }));
    if (!r.ok) { fail(r.error.message); return; }
    setList(r.data);
    setCur(prev => (prev && r.data.some(b => String(b.id) === prev)) ? prev : (r.data[0] ? String(r.data[0].id) : ''));
    if (!r.data.length) setDev(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void loadList(); }, [loadList]);

  const loadDev = useCallback(async () => {
    if (!cur) return;
    const r = await call<Deviations>('get',
      p('/api/projects/{pid}/report/deviations', { pid: ctx.pid }) + '?baseline=' + encodeURIComponent(cur));
    if (!r.ok) { fail(r.error.message); return; }
    setDev(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, cur, ctx.reloadToken]);
  useEffect(() => { void loadDev(); }, [loadDev]);

  async function addBaseline() {
    if (!name.trim()) { fail('Дайте базлайну название'); return; }
    const r = await call<{ id: number; zadach: number }>('post',
      p('/api/projects/{pid}/baselines', { pid: ctx.pid }), { name: name.trim() });
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.('');
    ctx.onSaved?.('Базовый план «' + name.trim() + '» зафиксирован: ' + r.data.zadach + ' задач.');
    setName('');
    void loadList();
  }

  function exportPlan(format: 'xlsx' | 'csv') {
    let token = '';
    try { token = localStorage.getItem('plot_t') || ''; } catch { /* приватный режим */ }
    window.open(p('/api/projects/{pid}/export', { pid: ctx.pid })
      + '?format=' + format + (token ? '&t=' + encodeURIComponent(token) : ''), '_blank');
  }

  const itogo = (dev?.itogo || {}) as { zadach?: number; pozzhe?: number; ranshe?: number };
  const rows = ((dev?.stroki || []) as DevRow[]).filter(x =>
    (!ctx.matchesWork || ctx.matchesWork(String(x.rabota || ''))) &&
    (!ctx.matchesKorpus || ctx.matchesKorpus(String(x.korpus || ''))));

  return (
    <div>
      <div className="bar actbar">
        <input placeholder="Название базлайна (напр. «Сентябрь-26»)" style={{ minWidth: 280 }}
          value={name} disabled={!can} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void addBaseline(); }} />
        {can && <button onClick={() => void addBaseline()}>Зафиксировать базлайн</button>}
        <span className="grow" />
        <button className="sec" onClick={() => exportPlan('xlsx')}>Выгрузить Excel</button>
        <button className="sec" onClick={() => exportPlan('csv')}>CSV</button>
      </div>
      <MspExport ctx={ctx} />
      <div className="bar">
        <label>Сравнивать с{' '}
          <select value={cur} onChange={e => setCur(e.target.value)}>
            {list.map(b => (
              <option key={b.id} value={b.id}>
                {b.name + ' (' + b.sozdan + (b.active ? ', активный' : '') + ')'}
              </option>
            ))}
          </select>
        </label>
        <span className="muted">
          {!list.length ? 'Базлайн ещё не зафиксирован — нажмите «Зафиксировать базлайн».'
            : dev ? '«' + (dev.baseline ?? '') + '»: задач ' + (itogo.zadach ?? 0)
              + ', позже базлайна ' + (itogo.pozzhe ?? 0) + ', раньше ' + (itogo.ranshe ?? 0) : ''}
        </span>
      </div>
      <div className="tbl" style={{ maxHeight: '66vh' }}>
        <table>
          <thead>
            <tr>
              <th>Корпус</th><th>Работа</th><th>Этажей</th><th>Позже базлайна</th>
              <th>Раньше</th><th>Макс. сдвиг, дн</th><th>Мин. сдвиг, дн</th>
            </tr>
          </thead>
          <tbody>
            {list.length > 0 && !rows.length && (
              <tr><td colSpan={7}>Отклонений нет (с учётом фильтра сверху).</td></tr>
            )}
            {rows.map((x, i) => (
              <tr key={i}>
                <td>{x.korpus}</td>
                <td>{x.rabota}</td>
                <td className="num">{x.etazhej}</td>
                <td className="num">{x.pozzhe}</td>
                <td className="num">{x.ranshe}</td>
                <td className="num">{x.maks_sdvig_dnej ?? ''}</td>
                <td className="num">{x.min_sdvig_dnej ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="muted" style={{ marginTop: 8 }}>
        Показаны только работы с отклонениями, отсортированы по величине опоздания.
        Выгрузка включает базовые сроки и отклонение по каждой задаче.
      </div>
    </div>
  );
}
